import React, { useEffect, useState } from 'react';
import logo from 'media/Logo/SLogo.svg';
import SVG from 'components/renderSvg';
import InputField from 'components/inputField';
import Button from 'components/button';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import emailIcon from '../../../media/svgs/email.svg';
import keyIcon from '../../../media/svgs/Key.svg';
import passwordIcon from '../../../media/svgs/password.svg';
import useAuth from 'hooks/useAuth';
import useDocument from 'hooks/useDocument';
import CustomSelect from 'components/customSelect';
import ProfileInputItem from 'pages/setup/components/profileInputItem';
import { registerSchema } from 'schema';
import { getCountries } from 'services/vendorAPI';
import SplitAuthLayout from '../components/SplitAuthLayout';
// Firebase functions removed - use backend API instead

const RegisterPage = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [countries, setCountries] = useState([]);
  const { loading } = useDocument('countries');

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countriesData = await getCountries();
        // getCountries returns an array on success, or an object with success: false on error
        if (Array.isArray(countriesData)) {
          setCountries(countriesData);
        } else {
          setCountries([]);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        setCountries([]);
      }
    };

    fetchCountries();
  }, []);

  const { registerUser } = useAuth();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      // name: '',
      email: '',
      password: '',
      confirm: '',
      country: '',
    },
  });

  const onSubmit = async data => {
    setLoader(true);
    const response = await registerUser(data);
    if (response?.success) {
      setLoader(false);
      navigate('/verify-otp', { state: { email: data.email } });
    } else {
      setLoader(false);
    }
  };

  return (
    <SplitAuthLayout>
      <div className='flex flex-col items-center mb-8'>
        <img src={logo} alt='Sportona Icon' className='w-12 h-12 mb-6 object-contain' />
        <h2 className='text-[28px] font-bold text-[#111] mb-2 text-center'>
          Create your business account
        </h2>
        <p className='text-[#666] text-center text-sm px-4'>
          Set up a Sportona account to manage your business.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4 w-full'>
        <InputField
          type='email'
          name='email'
          label='Email address'
          placeholder='Enter Email'
          error={errors?.email?.message}
          register={register}
          mb={false}
        />
        <ProfileInputItem label='Country'>
          <CustomSelect
            loading={loading}
            options={countries?.map(country => ({
              label: country.name || country.nameEn,
              value: country.code || country.id,
              icon: country.icon,
            }))}
            name='country'
            isMulti={false}
            formatOptionLabel={opt => (
              <div className="flex items-center gap-2">
                <img src={opt.icon} alt="" className="w-6 h-6" />
                <span>{opt.label}</span>
              </div>
            )}
            setSelectedValues={selectedCountry => {
              setValue('country', selectedCountry);
            }}
            error={errors?.country?.message}
          />
        </ProfileInputItem>
        <InputField
          type='password'
          label='Password'
          name='password'
          placeholder='Must be 8 digits long'
          error={errors?.password?.message}
          register={register}
          mb={false}
        />
        <InputField
          type='password'
          label='Confirm Password'
          name='confirm'
          placeholder='Must be 8 digits long'
          error={errors?.confirm?.message}
          register={register}
          mb={false}
        />
        <div className='flex items-center gap-2 mt-2'>
          <input
            type='checkbox'
            name='terms'
            className='size-4'
            {...register('terms')}
          />
          <label
            htmlFor='terms'
            className={errors?.terms ? 'text-red-500 text-sm' : 'text-[#666] text-sm'}
          >
            I agree to the{' '}
          </label>
          <span
            className='text-black text-sm font-medium cursor-pointer hover:underline'
            onClick={() =>
              window.open('https://sportona.com/terms-of-use', '_blank')
            }
          >
            Terms & Conditions
          </span>
        </div>
        <Button
          title='Create account'
          variant='primary'
          className='w-full mt-4 !bg-black !text-white'
          disabled={loader}
          style={{ backgroundColor: '#000000', color: '#ffffff' }}
        />

        <div className='mt-6 text-center text-sm text-gray-600'>
          Already have an account?{' '}
          <span
            className='font-semibold text-black cursor-pointer hover:underline'
            onClick={() => navigate('/login')}
          >
            Sign in
          </span>
        </div>
      </form>
    </SplitAuthLayout>
  );
};

export default RegisterPage;
